/**
 * Contract entity — contracts.Contract (xsite/contracts/models.py).
 *
 * The detail endpoint embeds its child rows (items/vehicles/staff/files/
 * offers) inline, so `useContract` treats those as "related models": any
 * WebSocket event for them refetches the whole contract (no separate entity
 * modeled per child yet — see src/shared/createResourceHooks.ts).
 */

import { createResourceApi } from '../shared/resourceApi';
import { createResourceHooks } from '../shared/createResourceHooks';
import type { Currency } from '../shared/types';

export interface Contract {
  id: number;
  name: string;
  contractor_name?: string | null;
  customer_name?: string | null;
  location?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  status_name?: string | null;
  budget?: number | string | null;
  currency_detail?: Currency | null;
  locked: boolean;
  // Nested sub-resources embedded by the detail serializer — not yet split
  // into their own entities, so left loosely typed here on purpose.
  items?: Array<Record<string, any>>;
  vehicles?: Array<Record<string, any>>;
  staff?: Array<Record<string, any>>;
  files?: Array<Record<string, any>>;
  offers?: Array<Record<string, any>>;
}

export const contractApi = createResourceApi<Contract>('contracts/api/contracts');

export const {
  keys: contractKeys,
  useList: useContractList,
  useDetail: useContract,
  useCreate: useCreateContract,
  useUpdate: useUpdateContract,
  useRemove: useDeleteContract,
} = createResourceHooks(contractApi, {
  name: 'contracts',
  model: 'contracts.contract',
  detailRelatedModels: [
    'contracts.contractitem',
    'contracts.contractitemallocation',
    'contracts.contractvehicle',
    'contracts.contractstaff',
    'contracts.contractfile',
    'contracts.contractoffer',
    'contracts.contractexternalitem',
  ],
  detailDebounceMs: 400,
});
